/**
 * Invitations — mint single-use signup links and watch what becomes of them.
 *
 * Every rule lives in the IdP (which apps exist, which levels, the immutable
 * address, single use, expiry). This view holds NO copy of any of them: the app
 * list arrives with the list response, and a refusal is rendered from the
 * server's own reason code. The one thing it knows on its own is that a minted
 * link is shown ONCE — the IdP keeps only a digest — so the reveal is a step the
 * admin has to dismiss rather than a row that quietly scrolls away.
 */

import {
  ApiClient,
  ApiError,
  type InvitationApp,
  type InvitationRecord,
  type InvitationStatus,
  type InvitationAppLevel,
  type InvitationCreateOutcome,
  type MintedInvitation,
} from '../lib/api.js';
import { el, mount, formatDateTime } from '../lib/dom.js';

export interface InvitationsViewDeps {
  api: ApiClient;
}

const PAGE_SIZE = 50;

/** What the admin chooses between. The IdP validates it again; this is the menu. */
const LEVELS: { value: InvitationAppLevel; label: string; help: string }[] = [
  { value: 'standard', label: 'Standard', help: 'An ordinary account on that app.' },
  { value: 'tester', label: 'Tester', help: 'Cleared for a closed beta — noa grants editor access to testers.' },
];

/**
 * The two statuses the admin asked for are `visited` and `redeemed`; the other
 * three are what make those two unambiguous. Colours follow the console's
 * existing pill vocabulary rather than inventing a scale.
 */
const STATUS: Record<InvitationStatus, { pill: string; label: string; help: string }> = {
  pending:  { pill: 'pill idle',    label: 'Not opened',  help: 'The link has never been opened.' },
  visited:  { pill: 'pill warning', label: 'Opened',      help: 'The page was opened, but no account was created.' },
  redeemed: { pill: 'pill success', label: 'Account created', help: 'Used — an account now exists.' },
  expired:  { pill: 'pill timeout', label: 'Expired',     help: 'The window closed before it was used.' },
  revoked:  { pill: 'pill failed',  label: 'Revoked',     help: 'Killed by an admin before use.' },
};

/** Why an address in the pasted list produced no link. */
const REASONS: Record<NonNullable<InvitationCreateOutcome['reason']>, string> = {
  'invalid-email': 'not a valid email address',
  'duplicate-in-list': 'listed twice — invited once',
  'already-invited': 'already has an open invitation (regenerate it to send a new link)',
  'already-redeemed': 'already used an invitation to this app',
};

export function renderInvitations(deps: InvitationsViewDeps): HTMLElement {
  let apps: InvitationApp[] = [];
  let filterApp = '';
  let offset = 0;

  const list = el('div', { class: 'volume-list' });
  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const appFilter = el('select', {}) as HTMLSelectElement;
  const searchInput = el('input', {
    attrs: { type: 'search', placeholder: 'Filter by email…', autocapitalize: 'off', spellcheck: false },
  }) as HTMLInputElement;
  const prevBtn = el('button', { class: 'ghost', text: '‹ Prev', attrs: { disabled: true } });
  const nextBtn = el('button', { class: 'ghost', text: 'Next ›', attrs: { disabled: true } });

  const err = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const ok = el('div', { class: 'alert success', attrs: { hidden: true, role: 'status' } });
  const flash = (node: HTMLElement, msg: string) => {
    node.textContent = msg;
    node.removeAttribute('hidden');
    setTimeout(() => node.setAttribute('hidden', ''), 6000);
  };
  const fail = (e: unknown) => flash(err, e instanceof ApiError ? e.message : 'Action failed');

  /* ── the compose form ──────────────────────────────────────────────────── */

  const emailsI = el('textarea', {
    attrs: {
      rows: '4',
      placeholder: 'alice@example.com, bob@example.com\nOne per line or comma-separated',
      spellcheck: 'false',
    },
  }) as HTMLTextAreaElement;
  const appI = el('select', {}) as HTMLSelectElement;
  const levelI = el('select', {}) as HTMLSelectElement;
  for (const l of LEVELS) levelI.append(el('option', { text: l.label, attrs: { value: l.value } }));
  const levelHelp = el('p', { class: 'help', text: LEVELS[0]!.help });
  levelI.addEventListener('change', () => {
    levelHelp.textContent = LEVELS.find((l) => l.value === levelI.value)?.help ?? '';
  });

  const sendI = el('input', { attrs: { type: 'checkbox' } }) as HTMLInputElement;
  const messageI = el('textarea', {
    attrs: { rows: '3', placeholder: 'Optional note, shown above the button in the email.', spellcheck: 'false' },
  }) as HTMLTextAreaElement;
  // The note only exists inside an email, so offering it while "send" is off
  // would be offering to write something nobody will read.
  const messageField = el('label', {
    class: 'form-field',
    attrs: { hidden: true },
    children: [el('span', { class: 'form-label', text: 'Message in the email' }), messageI],
  });
  sendI.addEventListener('change', () => {
    messageField.toggleAttribute('hidden', !sendI.checked);
    createBtn.textContent = sendI.checked ? 'Create & send' : 'Create links';
  });

  const expiryI = el('input', { attrs: { type: 'number', min: '1', max: '90', placeholder: '14' } }) as HTMLInputElement;
  const createBtn = el('button', { class: 'primary', text: 'Create links' });

  const field = (label: string, input: HTMLElement, help?: string) =>
    el('label', {
      class: 'form-field',
      children: [
        el('span', { class: 'form-label', text: label }),
        input,
        ...(help ? [el('p', { class: 'help', text: help })] : []),
      ],
    });

  createBtn.addEventListener('click', async () => {
    const emails = emailsI.value.trim();
    if (emails === '') {
      flash(err, 'Enter at least one email address.');
      return;
    }
    createBtn.setAttribute('disabled', '');
    try {
      const result = await deps.api.createInvitations({
        emails,
        app: appI.value,
        appLevel: levelI.value as InvitationAppLevel,
        sendEmail: sendI.checked,
        ...(sendI.checked && messageI.value.trim() !== '' ? { message: messageI.value.trim() } : {}),
        ...(expiryI.value !== '' ? { expiresInDays: Number(expiryI.value) } : {}),
      });
      showResult(result.results, result.created);
      emailsI.value = '';
      messageI.value = '';
      await load();
    } catch (e) {
      fail(e);
    } finally {
      createBtn.removeAttribute('disabled');
    }
  });

  /* ── the one-time reveal ───────────────────────────────────────────────── */

  /**
   * The links are shown ONCE, so this is a modal the admin must dismiss rather
   * than a toast. It also reports the addresses that produced nothing — a bulk
   * action that silently dropped part of its input is how an admin comes to
   * believe someone was invited when they were not.
   */
  const showResult = (results: InvitationCreateOutcome[], created: number) => {
    const minted = results.filter((r) => r.ok && r.invitation).map((r) => r.invitation!);
    const refused = results.filter((r) => !r.ok);

    const backdrop = el('div', { class: 'modal-backdrop' });
    const dismiss = () => { backdrop.remove(); document.removeEventListener('keydown', onKey); };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);

    const rows = minted.map((inv) => {
      const urlBox = el('pre', { class: 'log-output', text: inv.url });
      const copyBtn = el('button', {
        text: 'Copy link',
        on: {
          click: async () => {
            try {
              await navigator.clipboard.writeText(inv.url);
              copyBtn.textContent = 'Copied ✓';
              setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 2000);
            } catch {
              /* clipboard unavailable — the link is selectable above */
            }
          },
        },
      });
      const mailNote = inv.emailError
        ? el('span', { class: 'pill failed', text: `email failed: ${inv.emailError}` })
        : inv.emailSentAt
          ? el('span', { class: 'pill success', text: 'email sent' })
          : el('span', { class: 'pill idle', text: 'not emailed' });
      return el('div', {
        class: 'volume-row',
        children: [
          el('div', { class: 'volume-head', children: [
            el('span', { class: 'volume-name', text: inv.email }),
            el('span', { class: 'who', children: [mailNote] }),
          ] }),
          urlBox,
          el('div', { class: 'volume-actions', children: [copyBtn] }),
        ],
      });
    });

    const copyAllBtn = el('button', {
      class: 'ghost',
      text: 'Copy all (email + link)',
      on: {
        click: async () => {
          const text = minted.map((i) => `${i.email}\t${i.url}`).join('\n');
          try {
            await navigator.clipboard.writeText(text);
            copyAllBtn.textContent = 'Copied ✓';
            setTimeout(() => { copyAllBtn.textContent = 'Copy all (email + link)'; }, 2000);
          } catch {
            /* clipboard unavailable */
          }
        },
      },
    });

    const refusedBlock = refused.length > 0
      ? el('div', {
          children: [
            el('h4', { text: `${refused.length} address${refused.length === 1 ? '' : 'es'} produced no link` }),
            ...refused.map((r) => el('div', {
              class: 'volume-meta',
              text: `${r.email} — ${r.reason ? REASONS[r.reason] : 'refused'}`,
            })),
          ],
        })
      : el('span');

    const modal = el('div', {
      class: 'modal modal-wide',
      children: [
        el('header', { children: [
          el('div', { class: 'modal-title', children: [el('h3', { text: `${created} invitation${created === 1 ? '' : 's'} created` })] }),
          el('div', { class: 'log-actions', children: [el('button', { class: 'ghost', text: 'Close', on: { click: dismiss } })] }),
        ] }),
        el('div', { class: 'app-admin-body', children: [
          ...(minted.length > 0
            ? [el('div', {
                class: 'alert success',
                text: 'Copy these links now — they are shown only once. A lost link can be regenerated from the list below, which replaces it.',
              })]
            : []),
          ...(minted.length > 1 ? [el('div', { class: 'form-actions', children: [copyAllBtn] })] : []),
          ...rows,
          refusedBlock,
        ] }),
      ],
    });
    backdrop.append(modal);
    backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) dismiss(); });
    document.body.append(backdrop);
  };

  /** A regenerated link is one link, revealed the same way and for the same reason. */
  const showRegenerated = (inv: MintedInvitation) => {
    showResult([{ email: inv.email, ok: true, invitation: inv }], 1);
  };

  /* ── the list ──────────────────────────────────────────────────────────── */

  const appLabel = (id: string): string => apps.find((a) => a.id === id)?.label ?? id;

  const renderRow = (inv: InvitationRecord): HTMLElement => {
    const s = STATUS[inv.status];
    const badges: HTMLElement[] = [
      el('span', { class: s.pill, text: s.label }),
      el('span', { class: 'pill idle', text: appLabel(inv.app) }),
    ];
    if (inv.appLevel === 'tester') badges.push(el('span', { class: 'pill running', text: 'tester' }));
    // "Attached an existing account" is not "created one", and the difference
    // matters when an admin is auditing who they let in.
    if (inv.status === 'redeemed' && inv.redeemedExisting) {
      badges.push(el('span', { class: 'pill warning', text: 'existing account' }));
    }
    if (inv.emailError) badges.push(el('span', { class: 'pill failed', text: 'email failed' }));
    else if (inv.emailSentAt) badges.push(el('span', { class: 'pill idle', text: 'emailed' }));

    // The two statuses, spelled out rather than left to the badge colour.
    const meta: string[] = [
      `created ${formatDateTime(inv.createdAt)} by ${inv.createdByLabel}`,
      inv.visitedAt
        ? `opened ${formatDateTime(inv.visitedAt)}${inv.visitCount > 1 ? ` (${inv.visitCount}×)` : ''}`
        : 'never opened',
      inv.redeemedAt ? `account ${formatDateTime(inv.redeemedAt)}` : 'no account yet',
      inv.status === 'redeemed' || inv.status === 'revoked'
        ? ''
        : `expires ${formatDateTime(inv.expiresAt)}`,
    ].filter((x) => x !== '');

    const actions: HTMLElement[] = [];
    // A consumed or revoked invitation is finished: nothing to re-send, nothing
    // to kill. Offering the buttons anyway would only produce a server refusal.
    if (inv.status !== 'redeemed' && inv.status !== 'revoked') {
      actions.push(
        el('button', {
          text: 'New link',
          attrs: { title: 'Issue a fresh link. The previous one stops working.' },
          on: { click: () => void regenerate(inv, false) },
        }),
        el('button', {
          text: 'New link + email',
          attrs: { title: 'Issue a fresh link and email it to the invitee.' },
          on: { click: () => void regenerate(inv, true) },
        }),
        el('button', {
          class: 'danger',
          text: 'Revoke',
          on: { click: () => void revoke(inv) },
        }),
      );
    }
    if (inv.redeemedUserId) {
      actions.push(el('span', { class: 'volume-meta', text: `identity ${inv.redeemedUserId}` }));
    }

    return el('div', {
      class: 'volume-row',
      children: [
        el('div', { class: 'volume-head', children: [
          el('span', { class: 'volume-name', text: inv.email }),
          el('span', { class: 'who', children: badges }),
        ] }),
        el('div', { class: 'volume-meta', text: meta.join(' · ') }),
        ...(inv.emailError ? [el('div', { class: 'volume-meta', text: `email error: ${inv.emailError}` })] : []),
        el('div', { class: 'volume-actions', children: actions }),
      ],
    });
  };

  const regenerate = async (inv: InvitationRecord, sendEmail: boolean) => {
    try {
      const { invitation } = await deps.api.regenerateInvitation(inv.id, { sendEmail });
      showRegenerated(invitation);
      await load();
    } catch (e) {
      fail(e);
    }
  };

  const revoke = async (inv: InvitationRecord) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}? The link stops working immediately.`)) return;
    try {
      await deps.api.revokeInvitation(inv.id);
      flash(ok, `Invitation for ${inv.email} revoked.`);
      await load();
    } catch (e) {
      fail(e);
    }
  };

  const load = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const data = await deps.api.listInvitations({
        app: filterApp || undefined,
        email: searchInput.value.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      // The registry comes from the server on every load, so adding an app to
      // the suite reaches this screen without a release of the console.
      if (data.apps.length > 0 && apps.length !== data.apps.length) {
        apps = data.apps;
        syncAppSelects();
      } else if (apps.length === 0) {
        apps = data.apps;
        syncAppSelects();
      }
      const open = data.invitations.filter((i) => i.status === 'pending' || i.status === 'visited').length;
      summary.textContent = `${data.invitations.length} shown · ${open} still open`;
      mount(list, data.invitations.length > 0
        ? data.invitations.map(renderRow)
        : [el('div', { class: 'empty', text: 'No invitations yet.' })]);
      prevBtn.toggleAttribute('disabled', offset === 0);
      nextBtn.toggleAttribute('disabled', data.invitations.length < PAGE_SIZE);
    } catch (e) {
      mount(list, [el('div', { class: 'empty', text: e instanceof ApiError ? e.message : 'Failed to load invitations' })]);
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  /** Fill both app menus from the server's registry, keeping any selection. */
  function syncAppSelects(): void {
    const keepCompose = appI.value;
    const keepFilter = appFilter.value;
    appI.replaceChildren(...apps.map((a) => el('option', { text: a.label, attrs: { value: a.id } })));
    appFilter.replaceChildren(
      el('option', { text: 'All apps', attrs: { value: '' } }),
      ...apps.map((a) => el('option', { text: a.label, attrs: { value: a.id } })),
    );
    if (keepCompose && apps.some((a) => a.id === keepCompose)) appI.value = keepCompose;
    if (keepFilter && apps.some((a) => a.id === keepFilter)) appFilter.value = keepFilter;
  }

  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  searchInput.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { offset = 0; void load(); }, 250);
  });
  appFilter.addEventListener('change', () => { filterApp = appFilter.value; offset = 0; void load(); });
  refreshBtn.addEventListener('click', () => void load());
  prevBtn.addEventListener('click', () => { offset = Math.max(0, offset - PAGE_SIZE); void load(); });
  nextBtn.addEventListener('click', () => { offset += PAGE_SIZE; void load(); });

  void load();

  return el('div', {
    children: [
      err,
      ok,
      el('div', { class: 'card', children: [
        el('div', { class: 'toolbar', children: [
          el('h2', { text: 'Invite people', attrs: { style: 'margin:0' } }),
        ] }),
        el('p', { class: 'help', text: 'Each address gets its own single-use link. The invitee only chooses a password — the address is fixed and arrives already verified.' }),
        // `.app-form` is where this console's field/label styles are scoped;
        // outside it the labels render unstyled.
        el('div', { class: 'app-form', children: [
          field('Email addresses', emailsI, 'Commas, semicolons, spaces or newlines all work.'),
          el('div', { class: 'invite-grid', children: [
            field('App', appI),
            field('Account type', levelI),
            field('Valid for (days)', expiryI, 'Blank uses the default (14).'),
          ] }),
          levelHelp,
          el('label', { class: 'inline-check', children: [
            sendI,
            el('span', { class: 'form-label', text: 'Send the invitation email now' }),
          ] }),
          el('p', { class: 'help', text: 'Leave unchecked to get the links and send them yourself (chat, SMS, anywhere).' }),
          messageField,
          el('div', { class: 'form-actions', children: [createBtn] }),
        ] }),
      ] }),
      el('div', { class: 'card', children: [
        el('div', { class: 'toolbar', children: [
          el('h2', { text: 'Invitations', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          summary,
          appFilter,
          searchInput,
          refreshBtn,
        ] }),
        el('p', { class: 'help', text: 'Two separate things to watch: whether the link was opened, and whether an account exists. A link is shown once at creation — a lost one is replaced with “New link”.' }),
        list,
        el('div', { class: 'toolbar', children: [prevBtn, el('span', { class: 'spacer' }), nextBtn] }),
      ] }),
    ],
  });
}
